import { Box } from "@chakra-ui/react"
import React, { useEffect, useRef } from "react"

import { space } from "theme/themeConfig"
import { ErrorBoundary } from "react-error-boundary"
import { setGlobalNotifications, useNotifications } from "lib/notifications"
import { useHasMounted } from "utils/wrappers"
import type { UserNotificationType } from "types"

import { useRouter } from "lib/router"
import { Notification } from "./Notification"
import { NotificationFallback } from "./NotificationFallback"

const useShouldShowBanner = (notifications: UserNotificationType[]) => {
  const hasMounted = useHasMounted()

  const hasNotifications = notifications.length > 0

  return hasNotifications && hasMounted
}

export const NotificationsBanner: React.FC = () => {
  const notificationsBannerRef = useRef<HTMLDivElement | null>(null)
  const { globalNotifications: notifications } = useNotifications()

  const { pathname } = useRouter()
  const shouldShowBanner = useShouldShowBanner(notifications)
  useEffect(() => {
    setGlobalNotifications([])
  }, [pathname])

  useEffect(() => {
    if (!notificationsBannerRef.current) return

    notificationsBannerRef.current.scrollIntoView({
      block: "center",
      behavior: "smooth",
    })
  }, [notifications])

  if (!shouldShowBanner) return null

  const removeNotification = (code: string) =>
    notifications &&
    setGlobalNotifications(
      notifications.filter((notification) => notification.code !== code)
    )

  return (
    <ErrorBoundary FallbackComponent={() => <NotificationFallback />}>
      <Box ref={notificationsBannerRef} data-testid="notifications-banner">
        {notifications?.map((n) => (
          <Box p={[space["-3"], space[1]]} key={n.code}>
            <Notification
              notification={n}
              removeNotification={removeNotification}
            />
          </Box>
        ))}
      </Box>
    </ErrorBoundary>
  )
}
